import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { colors, radius } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import {
  aiGradientBorderCSS,
  aiGradientBorderFlowCSS,
  aiGradientOutlineCSS,
} from '@ardoq/style-helpers';
import styled, { css } from 'styled-components';

type AiFrameProps = {
  hasError?: boolean;
  hasWarning?: boolean;
  borderOnly?: boolean;
  rounded?: boolean;
  surfaceBackground?: boolean;
  isDisabled?: boolean;
  // Reveal the gradient outline only while focused, keeping
  // just the gradient border at rest
  outlineOnFocus?: boolean;
  // Animate the gradient border and outline
  // This automatically happens for inputs
  // when focused
  isActive?: boolean;
};

type TransientAiFrameProps = {
  $hasError: boolean;
  $hasWarning: boolean;
  $borderOnly: boolean;
  $rounded: boolean;
  $surfaceBackground?: boolean;
  $isDisabled: boolean;
  $outlineOnFocus: boolean;
  $isActive: boolean;
};

const flowingBorderCSS = ({
  $hasError,
  $hasWarning,
  $isDisabled,
  $isActive,
}: TransientAiFrameProps) => {
  if ($hasError || $hasWarning || $isDisabled) return null;
  return css`
    &:focus-within::before,
    &:focus-within::after {
      ${aiGradientBorderFlowCSS('6s', 'ease')}
    }

    ${
      $isActive &&
      css`
        &::before,
        &::after {
          ${aiGradientBorderFlowCSS('6s', 'ease')}
        }
      `
    }
  `;
};

const StyledAiFrame = styled(FlexBox)<TransientAiFrameProps>`
  position: relative;
  ${aiGradientBorderCSS}

  ${({ $borderOnly, $outlineOnFocus }) =>
    !$borderOnly &&
    css`
      ${aiGradientOutlineCSS}

      ${
        $outlineOnFocus &&
        css`
          &::after {
            opacity: 0;
            transition: opacity 0.2s ease;
          }

          &:focus-within::after {
            opacity: 0.4;
          }
        `
      }
    `}

  ${({ $rounded }) =>
    $rounded !== undefined &&
    css`
      border-radius: ${$rounded ? radius.medium : 0};
      &::before {
        border-radius: ${$rounded ? radius.medium : 0};
      }
    `}

  ${({ $surfaceBackground }) =>
    $surfaceBackground &&
    css`
      background: ${colors.surfaceDefault};

      && textarea,
      && input {
        background: ${colors.surfaceDefault};
      }
    `}

  /* paint both gradient layers above the nested control (eg autocomplete
  textarea uses z-index 2). The flow is static until the frame is focused or explicitly activated */
  &::before,
  &::after {
    z-index: 3;
  }

  ${flowingBorderCSS}

  /* strip the nested control's border and focus ring since we handle that now. The "&&" raises specificity above the form control's own focus styles */
  ${({ $borderOnly }) =>
    !$borderOnly &&
    css`
      && textarea,
      && input {
        box-shadow: none;
        outline: none;
        color: ${colors.textDefault};
      }

      && textarea:hover,
      && input:hover,
      && textarea:focus,
      && input:focus,
      && textarea:focus-visible,
      && input:focus-visible {
        box-shadow: none;
        outline: none;
      }
    `}

  ${({ $hasWarning, $hasError }) =>
    $hasWarning &&
    !$hasError &&
    css`
      &::before {
        background: ${colors.borderWarningStrong};
      }

      &::after {
        display: none;
      }
    `}

  ${({ $hasError }) =>
    $hasError &&
    css`
      &::before {
        background: ${colors.borderError};
      }

      &::after {
        display: none;
      }
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      background: ${colors.surfaceDisabled};

      &::before {
        background: ${colors.borderSubtle};
      }

      &::after {
        display: none;
      }

      && textarea,
      && input {
        color: ${colors.textDisabled};
        background: ${colors.surfaceDisabled};
      }
    `}
`;

export const AiFrame = forwardRef<
  HTMLElement,
  AiFrameProps & ComponentPropsWithoutRef<typeof FlexBox>
>(
  (
    {
      hasError,
      hasWarning,
      borderOnly,
      rounded,
      surfaceBackground,
      isDisabled,
      outlineOnFocus,
      isActive,
      ...rest
    },
    forwardedRef
  ) => {
    return (
      <StyledAiFrame
        ref={forwardedRef}
        {...rest}
        $hasError={Boolean(hasError)}
        $hasWarning={Boolean(hasWarning)}
        $borderOnly={Boolean(borderOnly)}
        $rounded={Boolean(rounded)}
        $surfaceBackground={Boolean(surfaceBackground)}
        $isDisabled={Boolean(isDisabled)}
        $outlineOnFocus={Boolean(outlineOnFocus)}
        $isActive={Boolean(isActive)}
      />
    );
  }
);

AiFrame.displayName = 'AiFrame';
